import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  bannerCssVars,
  type BannerDisplayOptions,
} from '../utils/bannerDisplay';
import {
  buildBannerSlides,
  cycleDurationMs,
  ENTER_MS,
  LEAVE_MS,
  slideDurationMs,
} from '../utils/bannerSlides';

type Stage = 'hidden' | 'entering' | 'visible' | 'leaving';

type Props = {
  code: string;
  bonusDays: number;
  opts: BannerDisplayOptions;
  /** Wrap in OBS-sized frame (600×160). Default true. */
  framed?: boolean;
  className?: string;
  /** Slightly faster carousel for editing UI without changing OBS URL timing. */
  pace?: 'realtime' | 'preview';
};

export function BannerLivePreview({
  code,
  bonusDays,
  opts,
  framed = true,
  className = '',
  pace = 'realtime',
}: Props) {
  const slides = useMemo(
    () => buildBannerSlides(code, bonusDays, opts),
    [code, bonusDays, opts],
  );
  const cssVars = useMemo(() => bannerCssVars(opts), [opts]);
  const [slideIndex, setSlideIndex] = useState(0);
  const [phase, setPhase] = useState<'in' | 'out'>('in');
  const [stage, setStage] = useState<Stage>(opts.mode === 'static' ? 'visible' : 'hidden');
  const enterFrom = slideIndex % 2 === 0 ? 'right' : 'left';
  const paceFactor = pace === 'preview' ? 0.45 : 1;

  // Reset when layout/text changes so editor sees the new first slide quickly.
  useEffect(() => {
    setSlideIndex(0);
    setPhase('in');
    if (opts.mode === 'static') setStage('visible');
  }, [opts.layout, opts.text, opts.mode]);

  useEffect(() => {
    if (slides.length < 2) return;
    if (stage !== 'visible' && stage !== 'entering') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let swapTimer = 0;
    const dwell = slideDurationMs(slideIndex, slides.length) * paceFactor;
    const tick = window.setTimeout(() => {
      setPhase('out');
      swapTimer = window.setTimeout(() => {
        setSlideIndex((i) => (i + 1) % slides.length);
        setPhase('in');
      }, 420 * paceFactor);
    }, dwell);

    return () => {
      window.clearTimeout(tick);
      window.clearTimeout(swapTimer);
    };
  }, [slides.length, stage, slideIndex, paceFactor]);

  useEffect(() => {
    if (opts.mode === 'static') {
      setStage('visible');
      return;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setStage('visible');
      return;
    }

    let cancelled = false;
    const timers: number[] = [];
    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        timers.push(window.setTimeout(resolve, ms));
      });

    const slideCount = slides.length || 6;
    const oneCycle = cycleDurationMs(slideCount) * paceFactor;
    const holdSec = Math.max(5, opts.holdSec) * paceFactor;
    const everySec = Math.max(3, opts.everySec) * paceFactor;
    const cycles = Math.max(1, Math.round((holdSec * 1000) / oneCycle));
    const holdMs = cycles * oneCycle;

    (async () => {
      while (!cancelled) {
        setSlideIndex(0);
        setPhase('in');
        setStage('entering');
        await wait(ENTER_MS * paceFactor);
        if (cancelled) break;
        setStage('visible');
        await wait(holdMs);
        if (cancelled) break;
        setStage('leaving');
        await wait(LEAVE_MS * paceFactor);
        if (cancelled) break;
        setStage('hidden');
        await wait(everySec * 1000);
      }
    })();

    return () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [slides.length, opts.mode, opts.holdSec, opts.everySec, opts.from, paceFactor]);

  const slide = slides[slideIndex] ?? slides[0];
  const isSimple = opts.layout === 'simple';

  const banner = (
    <div
      className={`streamer-banner-root is-${stage} from-${opts.mode === 'enter' ? opts.from : 'center'}${opts.fx ? '' : ' no-fx'}${isSimple ? ' is-simple' : ''}`}
      data-mode={opts.mode}
      style={cssVars as CSSProperties}
    >
      <div
        className="streamer-banner"
        role="img"
        aria-label={`Твой провайдер — промокод ${code}`}
      >
        {opts.fx ? (
          <>
            <span className="streamer-banner-border" aria-hidden />
            <span className="streamer-banner-glow" aria-hidden />
            <span className="streamer-banner-orb streamer-banner-orb-a" aria-hidden />
            <span className="streamer-banner-orb streamer-banner-orb-b" aria-hidden />
            <span className="streamer-banner-shine" aria-hidden />
            <span className="streamer-banner-scan" aria-hidden />
          </>
        ) : (
          <span className="streamer-banner-border" aria-hidden />
        )}
        <div className="streamer-banner-content">
          {!isSimple ? (
            <div key={slideIndex} className="streamer-banner-brand">
              Твой провайдер
            </div>
          ) : null}
          {slide ? (
            <div
              className={`streamer-banner-slide is-${slide.tone} from-${enterFrom} is-${phase}`}
              key={`${slide.id}-${slideIndex}`}
            >
              <p className="streamer-banner-line">{slide.text}</p>
              {slide.tone === 'promo' && !opts.text.trim() ? (
                <div className="streamer-banner-code">{slide.code}</div>
              ) : null}
              {isSimple && !slide.text.includes(code) ? (
                <div className="streamer-banner-code">{code}</div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  if (!framed) return banner;

  return (
    <div className={`banner-preview-frame${className ? ` ${className}` : ''}`}>
      {banner}
      <span className="banner-preview-badge">Live · 600×160</span>
    </div>
  );
}
